import type React from "react";
import { toast } from "react-toastify";
import type { TournamentDetailResponse } from "../../../@apis/dto/response/torunament.response";
import { formatDate } from "../../../lib/utils/dateUtils";
import Button from "../button";
import BottomDrawer from ".";

interface DepositAccountDrawerProps {
  open: boolean;
  onClose: () => void;
  item: TournamentDetailResponse;
}

interface AccountBoxData {
  label?: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  deadline?: string;
}

// 라벨/값 한 줄
function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-[8px] items-start text-sm font-medium">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      {children}
    </div>
  );
}

export default function DepositAccountDrawer({
  open,
  onClose,
  item,
}: DepositAccountDrawerProps) {
  const paymentInfo = item.paymentInfo;
  const entryFee = item.tournamentInfo.entryFee ?? 0;
  const amount = paymentInfo?.amt ?? entryFee;
  // 가상계좌(사용자별 발급) 정보가 있으면 가상계좌, 없으면 무통장입금(대회 계좌)
  const isVbank = !!paymentInfo?.vBankNum;
  // 계좌 박스 입금기한: 2026-07-08 (금) 23:59
  const deadline =
    isVbank && paymentInfo?.vBankExpDt
      ? formatDate(paymentInfo.vBankExpDt, "YYYY-MM-DD (ddd) HH:mm")
      : undefined;
  // 안내 문구 입금기한: 7월 8일 23:59
  const deadlineMessage =
    isVbank && paymentInfo?.vBankExpDt
      ? formatDate(paymentInfo.vBankExpDt, "M월 D일 HH:mm")
      : undefined;

  const accounts: AccountBoxData[] = isVbank
    ? [
        {
          bankName: paymentInfo?.vBankBankName ?? "",
          accountNumber: paymentInfo?.vBankNum ?? "",
          accountHolder: paymentInfo?.vBankAccountHolder ?? "",
          deadline,
        },
      ]
    : (item.tournamentInfo.accounts ?? [])
        .filter((a) => a.isUse !== false)
        .map((a) => ({
          label: a.label,
          bankName: a.bankName,
          accountNumber: a.accountNumber,
          accountHolder: a.accountHolder,
        }));

  const handleCopy = async (accountNumber: string) => {
    try {
      await navigator.clipboard?.writeText(accountNumber);
      toast("복사되었습니다.");
    } catch {
      toast("복사에 실패했습니다.");
    }
  };

  return (
    <BottomDrawer open={open} onClose={onClose}>
      <div className="flex flex-col items-center gap-[12px]">
        {/* 안내 */}
        <div className="bg-[#ff3b30] size-10 rounded-full flex items-center justify-center">
          <span className="text-white text-3xl font-bold leading-none">!</span>
        </div>
        <div className="flex flex-col items-center gap-[4px] w-full text-center">
          <p className="text-xl font-medium text-black">입금대기중</p>
          <div className="text-base font-semibold text-[#8e8e93] break-keep">
            {deadlineMessage && (
              <p>
                <span className="text-black">{deadlineMessage}</span>
                까지 아래 계좌로 입금해 주세요.
                <br />
              </p>
            )}

            <p className="whitespace-pre-line break-keep">
              {isVbank ? (
                <>
                  입금기한까지 결제가 완료되지 않으면 접수가 자동으로
                  취소됩니다.
                  {"\n"}
                  <span className="text-[#ff3b30]">
                    입금 확인까지는 10분 정도 소요될 수 있습니다.
                  </span>
                </>
              ) : (
                "입금 확인 후 순차적으로 승인 처리됩니다."
              )}
            </p>
          </div>
        </div>

        <div className="w-full h-px bg-[#e5e5ea]" />

        {/* 입금 계좌 정보 */}
        <div className="w-full flex flex-col gap-[8px]">
          {accounts.length > 0 ? (
            accounts.map((account, idx) => (
              <div
                key={`${account.accountNumber}-${idx}`}
                className="bg-[#f2f2f7] rounded-[10px] p-[8px] flex flex-col gap-[8px]"
              >
                {(isVbank || account.label) && (
                  <p className="text-sm font-semibold text-black">
                    {isVbank ? "가상계좌" : account.label}
                  </p>
                )}
                <Row label="입금계좌">
                  <p className="flex-1 text-black break-all">
                    {account.bankName} {account.accountNumber}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleCopy(account.accountNumber)}
                    className="shrink-0 rounded-[5px] border border-[#8e8e93] bg-white text-xs font-medium text-black py-[2px] px-[10px]"
                  >
                    복사
                  </button>
                </Row>
                <Row label="예금주명">
                  <p className="flex-1 text-black break-keep">
                    {account.accountHolder}
                  </p>
                </Row>
                {account.deadline && (
                  <Row label="입금기한">
                    <p className="flex-1 text-black break-keep">
                      {account.deadline}
                    </p>
                  </Row>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-[#8e8e93] py-[8px] text-center">
              입금 계좌 정보가 없습니다.
            </p>
          )}

          <div className="bg-[#f2f2f7] rounded-[10px] p-[12px] flex items-center justify-between">
            <p className="text-sm font-semibold text-black">입금금액</p>
            <p className="text-base font-semibold text-[#007aff]">
              {amount.toLocaleString()}원
            </p>
          </div>
        </div>

        <Button
          className="w-full"
          variant="white"
          onClick={onClose}
          label="닫기"
        />
      </div>
    </BottomDrawer>
  );
}
